<template>
  <div class="barcode-scanner">
    <div class="scanner-header">
      <h3>Barcode Scanner Test</h3>
      <p class="scanner-status" :class="{ 'active': isScanning }">
        {{ isScanning ? 'Camera Active' : 'Camera Inactive' }}
      </p>
    </div>

    <div class="scanner-container">
      <div class="camera-section">
        <div v-if="!isScanning" class="camera-controls">
          <button @click="startScanning" class="btn btn-primary">
            <i class="icon">📷</i>
            Start Camera
          </button>
          
          <div class="camera-info">
            <p>Click "Start Camera" to begin scanning barcodes</p>
            <p class="note">Note: This requires camera permissions</p>
          </div>
        </div>

        <div v-else class="camera-viewport">
                     <qrcode-stream
             :paused="!isScanning"
             @detect="onDetect"
             @error="onError"
             @camera-on="onInit"
             class="qr-reader"
           />
          
          <div class="scanner-overlay">
            <div class="scan-area">
              <div class="scan-line"></div>
            </div>
          </div>
          
          <div class="camera-controls">
            <button @click="stopScanning" class="btn btn-secondary">
              <i class="icon">⏹️</i>
              Stop Camera
            </button>
          </div>
        </div>
      </div>

      <div class="results-section">
        <h4>Scan Results</h4>
        
        <div v-if="loading" class="loading">
          <div class="spinner"></div>
          <p>Initializing camera...</p>
        </div>
        
        <div v-if="error" class="error">
          <p><strong>Error:</strong> {{ error }}</p>
          <button @click="clearError" class="btn btn-small">Clear Error</button>
        </div>
        
        <div v-if="lastScan" class="scan-result success">
          <h5>Latest Scan:</h5>
          <p class="barcode-value">{{ lastScan.text }}</p>
          <p class="scan-time">{{ formatTime(lastScan.timestamp) }}</p>
        </div>
        
        <div v-if="scanHistory.length > 0" class="scan-history">
          <h5>Scan History:</h5>
          <div class="history-list">
            <div 
              v-for="(scan, index) in scanHistory.slice(0, 5)" 
              :key="index"
              class="history-item"
            >
              <span class="barcode">{{ scan.text }}</span>
              <span class="timestamp">{{ formatTime(scan.timestamp) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="scanner-info">
      <h4>Test Instructions:</h4>
      <ul>
        <li>Click "Start Camera" to activate the barcode scanner</li>
        <li>Point your camera at any barcode or QR code</li>
        <li>The scanner will automatically detect and display the code</li>
        <li>Test with product barcodes, QR codes, or any readable codes</li>
        <li>Check the scan history to see all detected codes</li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref, onUnmounted } from 'vue'
import { QrcodeStream } from 'vue-qrcode-reader'

// State
const isScanning = ref(false)
const loading = ref(false)
const error = ref(null)
const lastScan = ref(null)
const scanHistory = ref([])

// Camera control methods
const startScanning = () => {
  isScanning.value = true
  loading.value = true
  error.value = null
}

const stopScanning = () => {
  isScanning.value = false
  loading.value = false
}

// Scanner event handlers
const onDetect = (detectedCodes) => {
  if (detectedCodes.length > 0) {
    const code = detectedCodes[0]
    const scanData = {
      text: code.rawValue,
      timestamp: new Date(),
      format: code.format || 'Unknown'
    }
    
    lastScan.value = scanData
    scanHistory.value.unshift(scanData)
    
    // Keep only last 10 scans
    if (scanHistory.value.length > 10) {
      scanHistory.value = scanHistory.value.slice(0, 10)
    }
    
    // Optional: Auto-stop after successful scan
    // stopScanning()
  }
}

const onError = (err) => {
  console.error('Scanner error:', err)
  error.value = err.message || 'Camera error occurred'
  loading.value = false
}

const onInit = () => {
  loading.value = false
  error.value = null
}

const clearError = () => {
  error.value = null
}

const formatTime = (timestamp) => {
  return timestamp.toLocaleTimeString()
}

// Cleanup on component unmount
onUnmounted(() => {
  stopScanning()
})
</script>

<style scoped>
.barcode-scanner {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.scanner-header {
  text-align: center;
  margin-bottom: 30px;
}

.scanner-header h3 {
  color: #333;
  margin-bottom: 10px;
}

.scanner-status {
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 500;
  display: inline-block;
  background: #f5f5f5;
  color: #666;
  transition: all 0.3s ease;
}

.scanner-status.active {
  background: #4CAF50;
  color: white;
}

.scanner-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 30px;
}

.camera-section {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.camera-controls {
  text-align: center;
  margin-bottom: 20px;
}

.camera-info {
  margin-top: 20px;
}

.camera-info p {
  margin: 8px 0;
  color: #666;
}

.note {
  font-size: 0.9em;
  font-style: italic;
}

.camera-viewport {
  position: relative;
  height: 300px;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
}

.qr-reader {
  width: 100%;
  height: 100%;
}

.scanner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  display: flex;
  justify-content: center;
  align-items: center;
}

.scan-area {
  width: 200px;
  height: 200px;
  border: 2px solid #4CAF50;
  border-radius: 8px;
  position: relative;
  box-shadow: 0 0 0 1000px rgba(0, 0, 0, 0.3);
}

.scan-line {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: #4CAF50;
  animation: scan 2s linear infinite;
}

@keyframes scan {
  0% { top: 0; }
  100% { top: 100%; }
}

.results-section {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 20px;
}

.results-section h4 {
  margin-top: 0;
  color: #333;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 10px;
}

.loading {
  text-align: center;
  padding: 20px;
  color: #666;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #4CAF50;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error {
  background: #ffebee;
  border: 1px solid #f44336;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
}

.error p {
  margin: 0 0 10px 0;
  color: #d32f2f;
}

.scan-result {
  background: #e8f5e8;
  border: 1px solid #4CAF50;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
}

.scan-result h5 {
  margin: 0 0 10px 0;
  color: #2e7d32;
}

.barcode-value {
  font-family: 'Courier New', monospace;
  font-size: 1.1em;
  font-weight: bold;
  color: #1976d2;
  margin: 8px 0;
  word-break: break-all;
}

.scan-time {
  font-size: 0.9em;
  color: #666;
  margin: 0;
}

.scan-history h5 {
  margin: 0 0 10px 0;
  color: #333;
}

.history-list {
  max-height: 150px;
  overflow-y: auto;
}

.history-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.history-item:last-child {
  border-bottom: none;
}

.barcode {
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
  color: #1976d2;
  flex: 1;
  margin-right: 10px;
  word-break: break-all;
}

.timestamp {
  font-size: 0.8em;
  color: #888;
  flex-shrink: 0;
}

.scanner-info {
  background: #f0f8ff;
  border: 1px solid #2196f3;
  border-radius: 8px;
  padding: 20px;
}

.scanner-info h4 {
  margin-top: 0;
  color: #1976d2;
}

.scanner-info ul {
  margin: 10px 0;
  padding-left: 20px;
}

.scanner-info li {
  margin: 8px 0;
  color: #333;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-primary {
  background: #4CAF50;
  color: white;
}

.btn-primary:hover {
  background: #45a049;
}

.btn-secondary {
  background: #f44336;
  color: white;
}

.btn-secondary:hover {
  background: #da190b;
}

.btn-small {
  padding: 6px 12px;
  font-size: 0.9em;
}

.icon {
  font-size: 1.2em;
}

@media (max-width: 768px) {
  .scanner-container {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .camera-viewport {
    height: 250px;
  }
  
  .scan-area {
    width: 150px;
    height: 150px;
  }
}
</style> 